import React, { useState, useEffect } from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal
} from '@floating-ui/react';

interface ColorPickerProps {
  value: string;
  onChange: (value: string) => void;
}

const ColorPicker: React.FC<ColorPickerProps> = ({ value, onChange }) => {
  const [hexInput, setHexInput] = useState('');
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [hue, setHue] = useState(0);
  const [saturation, setSaturation] = useState(100);
  const [lightness, setLightness] = useState(50);

  // Floating UI setup
  const { refs, floatingStyles, context } = useFloating({
    open: isPickerOpen,
    onOpenChange: setIsPickerOpen,
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 8 })
    ],
    whileElementsMounted: autoUpdate,
    placement: 'bottom-start'
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role
  ]);

  // Convert RGB/RGBA to HEX (6 characters only, no alpha)
  const rgbToHex = (rgb: string): string => {
    if (!rgb) return rgb;
    
    if (rgb.startsWith('#')) {
      // If it's already hex, ensure it's only 6 characters (remove alpha if present)
      if (rgb.length === 9) {
        // 8-character hex with alpha, strip the alpha
        return rgb.substring(0, 7).toUpperCase();
      }
      return rgb.toUpperCase();
    }
    
    // Handle both rgb() and rgba() formats
    const rgbMatch = rgb.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
    if (rgbMatch) {
      const r = parseInt(rgbMatch[1]);
      const g = parseInt(rgbMatch[2]);
      const b = parseInt(rgbMatch[3]);
      return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase();
    }
    return rgb;
  };

  // Convert HEX to HSL
  const hexToHsl = (hex: string): [number, number, number] => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }

    return [h * 360, s * 100, l * 100];
  };

  // Convert HSL to HEX
  const hslToHex = (h: number, s: number, l: number): string => {
    h = h / 360;
    s = s / 100;
    l = l / 100;

    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const r = hue2rgb(p, q, h + 1/3);
    const g = hue2rgb(p, q, h);
    const b = hue2rgb(p, q, h - 1/3);

    const toHex = (c: number) => {
      const hex = Math.round(c * 255).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
  };

  // Update HSL when value changes
  useEffect(() => {
    const hexValue = rgbToHex(value);
    setHexInput(hexValue);
    if (hexValue && hexValue.length === 7) {
      const [h, s, l] = hexToHsl(hexValue);
      setHue(h);
      setSaturation(s);
      setLightness(l);
    }
  }, [value]);

  // Validate HEX color
  const isValidHex = (hex: string): boolean => {
    return /^#[0-9A-Fa-f]{6}$/.test(hex);
  };

  // Normalize hex input
  const normalizeHex = (input: string): string => {
    let normalized = input.trim();
    
    // Extract hex color from CSS property format (e.g., "border-color: #1f2937ed;")
    const cssColorMatch = normalized.match(/#[0-9A-Fa-f]{6,8}/);
    if (cssColorMatch) {
      normalized = cssColorMatch[0];
    }
    
    normalized = normalized.toUpperCase();
    
    // Remove any non-hex characters except #
    normalized = normalized.replace(/[^#0-9A-F]/g, '');
    
    if (!normalized.startsWith('#')) {
      normalized = '#' + normalized;
    }
    
    // Handle 3-character hex codes (e.g., #ABC -> #AABBCC)
    if (/^#[0-9A-F]{3}$/.test(normalized)) {
      normalized = '#' + normalized[1] + normalized[1] + normalized[2] + normalized[2] + normalized[3] + normalized[3];
    }
    
    // Handle 8-character hex codes (with alpha) - strip alpha
    if (/^#[0-9A-F]{8}$/.test(normalized)) {
      normalized = normalized.substring(0, 7);
    }
    
    return normalized;
  };

  // Handle hex input changes
  const handleHexChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setHexInput(newValue);
    
    const normalized = normalizeHex(newValue);
    if (isValidHex(normalized)) {
      onChange(normalized);
    }
  };

  // Handle paste events
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = e.clipboardData.getData('text');
    const normalized = normalizeHex(pastedText);
    
    if (isValidHex(normalized)) {
      e.preventDefault();
      setHexInput(normalized);
      onChange(normalized);
    } else {
      console.log('Invalid pasted hex color:', pastedText, 'normalized:', normalized);
    }
  };

  // Handle HSL changes
  const handleHslChange = (newHue: number, newSat: number, newLight: number) => {
    setHue(newHue);
    setSaturation(newSat);
    setLightness(newLight);
    
    const newHex = hslToHex(newHue, newSat, newLight);
    setHexInput(newHex);
    onChange(newHex);
  };

  // Common colors
  const commonColors = [
    '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF',
    '#FF8000', '#8000FF', '#0080FF', '#80FF00', '#FF0080', '#00FF80',
    '#800000', '#008000', '#000080', '#808000', '#800080', '#008080',
    '#000000', '#404040', '#808080', '#C0C0C0', '#FFFFFF', '#F0F0F0'
  ];

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {/* HEX Input */}
        <div style={{ flex: 1 }}>
          <input
            type="text"
            value={hexInput}
            onChange={handleHexChange}
            onPaste={handlePaste}
            onFocus={(e) => {
              e.target.style.borderColor = '#667eea';
              e.target.style.backgroundColor = '#f8fafc';
              e.target.select();
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e5e7eb';
              e.target.style.backgroundColor = 'white';
            }}
            onKeyDown={(e) => {
              // Allow paste with Ctrl+V
              if (e.ctrlKey && e.key === 'v') {
                // Browser will handle paste automatically
                return;
              }
            }}
            placeholder="#000000"
            maxLength={7}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '2px solid #e5e7eb',
              borderRadius: 8,
              fontSize: 14,
              fontFamily: 'monospace',
              fontWeight: 600,
              textAlign: 'center',
              textTransform: 'uppercase',
              outline: 'none',
              transition: 'border-color 0.2s',
              backgroundColor: 'white'
            }}
          />
          <div style={{
            fontSize: 10,
            color: '#6b7280',
            textAlign: 'center',
            marginTop: 4
          }}>
            HEX Color (paste supported)
          </div>
        </div>
        
        {/* Color Swatch Button */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <button
            ref={refs.setReference}
            {...getReferenceProps()}
            style={{
              width: 44,
              height: 44,
              backgroundColor: rgbToHex(value),
              border: '2px solid #e5e7eb',
              borderRadius: 8,
              cursor: 'pointer',
              padding: 0,
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            onMouseEnter={(e) => (e.target as HTMLButtonElement).style.borderColor = '#667eea'}
            onMouseLeave={(e) => (e.target as HTMLButtonElement).style.borderColor = '#e5e7eb'}
          />
          <div style={{
            fontSize: 10,
            color: '#9ca3af',
            textAlign: 'center',
            marginTop: 4
          }}>
            Picker
          </div>
        </div>
      </div>

      {/* Floating Color Picker */}
      <FloatingPortal>
        {isPickerOpen && (
          <div 
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              width: 280,
              zIndex: 999999,
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              padding: 16,
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)'
            }}
            {...getFloatingProps()}
          >
            {/* Hue Slider */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Hue</div>
              <input
                type="range"
                min="0"
                max="360"
                value={hue}
                onChange={(e) => handleHslChange(Number(e.target.value), saturation, lightness)}
                style={{
                  width: '100%',
                  height: 8,
                  background: 'linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)',
                  borderRadius: 4,
                  outline: 'none',
                  border: 'none'
                }}
              />
            </div>

            {/* Saturation Slider */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Saturation</div>
              <input
                type="range"
                min="0"
                max="100"
                value={saturation}
                onChange={(e) => handleHslChange(hue, Number(e.target.value), lightness)}
                style={{
                  width: '100%',
                  height: 8,
                  background: `linear-gradient(to right, hsl(${hue}, 0%, ${lightness}%), hsl(${hue}, 100%, ${lightness}%))`,
                  borderRadius: 4,
                  outline: 'none',
                  border: 'none'
                }}
              />
            </div>

            {/* Lightness Slider */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Lightness</div>
              <input
                type="range"
                min="0"
                max="100"
                value={lightness}
                onChange={(e) => handleHslChange(hue, saturation, Number(e.target.value))}
                style={{
                  width: '100%',
                  height: 8,
                  background: `linear-gradient(to right, hsl(${hue}, ${saturation}%, 0%), hsl(${hue}, ${saturation}%, 50%), hsl(${hue}, ${saturation}%, 100%))`,
                  borderRadius: 4,
                  outline: 'none',
                  border: 'none'
                }}
              />
            </div>

            {/* Common Colors */}
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Common Colors</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 4 }}>
                {commonColors.map((color) => (
                  <button
                    key={color}
                    onClick={() => {
                      setHexInput(color);
                      onChange(color);
                      setIsPickerOpen(false);
                    }}
                    style={{
                      width: 24,
                      height: 24,
                      backgroundColor: color,
                      border: rgbToHex(value) === color ? '2px solid #667eea' : '1px solid #e5e7eb',
                      borderRadius: 4,
                      cursor: 'pointer',
                      outline: 'none'
                    }}
                    title={color}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </FloatingPortal>
    </div>
  );
};

export default ColorPicker;